import React, { useContext, useEffect, useRef, useState } from 'react'
import PropTypes from 'prop-types'
import queryString from 'query-string'
import {
  Box,
  Checkbox,
  Flex,
  Label,
  Link,
  Radio,
  Text
} from 'pcln-design-system'
import styled from 'styled-components'
import { Calendar } from '@pcln/date-picker'
import { PopoverContainer } from '../Popover'
import { TravelerSelection } from '../traveler-selection'
import TypeAhead from '../type-ahead/TypeAhead'
import DateField from '../date-field'
import TimeField from '../time-field'
import { pathOr, pipe, isEmpty } from 'ramda'
import { CalendarWrapperBox, ShadowEffect, FindYourDealButton } from '../../shared'
import * as PCLNAnalytics from '../../utils/PCLN-Analytics'
import makeFeedbackAjaxCall from '../../utils/typeahead-feedback'
import { formatDate, getPackagesDateRange, getMaxDate, getMinDate } from '../../utils/date-helpers'
import { BootstrapDataContext } from '../../contexts'
import { CALENDAR_POPOVER_SELECTOR, TRIPS_PRODUCT_TYPE, ANALYTICS_CATEGORY } from '../../constants/Constants'
import { useSeti } from '../hooks'
import OpenTripsWithPrepopLink from './OpenTripsWithPrepopLink'

const config = require('isomorphic-config')
const typeaheadFeedbackURL = config.client.typeahead.feedback.packages
const typeaheadFeedback = makeFeedbackAjaxCall(typeaheadFeedbackURL)

const VP_DISCLAIMER = 'You may only book a package for up to 8 travelers.'
const noop = () => {}

// parse returned object from Traveler Selection
// since it doesn't match the value provided
const handleTravelerSelectionChange = setFieldValue => obj => {
  if (obj.childAges) {
    const arr = Object.keys(obj.childAges).map(key => obj.childAges[key])
    setFieldValue('childrenAges', arr)
    return
  }
  switch (obj.travelerType) {
    case 'ROOMS':
      setFieldValue('roomCount', obj.count)
      break
    case 'ADULTS':
      setFieldValue('adultCount', obj.count)
      break
    case 'CHILDREN':
      setFieldValue('childrenCount', obj.count)
      break
  }
}

const parseDateError = (touched, errors) => {
  const evalStartDate = () => touched.startDate && errors.startDate
  const evalEndDate = () => touched.endDate && errors.endDate

  return evalStartDate() || evalEndDate()
}

const parsePartialDateError = (touched, errors) =>
  (touched.hotelStartDate && errors.hotelStartDate) ||
  (touched.hotelEndDate && errors.hotelEndDate)

const hasHotel = values => values.tripType.includes('STAY')
const travelerSelectionDisplayLabel = (hasHotel, values) => {
  const travelers = `${values.adultCount} Adults `
  const rooms = hasHotel ? `, ${values.roomCount} Room` : ''
  return travelers + rooms
}

const isHotelPlusCar = values => values.tripType === 'STAY:DRIVE'

const TripTypeLabel = styled(Label)`
  cursor: pointer;
`

const InlineBlockBox = styled(Box)`
  display: inline-block;
`

const startLocationPlaceholder = pathOr(
  'Where from?',
  [ 'contentValues', 'HOMEPAGE', 'HOMEPAGE.PACKAGES.TAB.TYPEAHEAD.STARTLOCATION.PLACEHOLDER', 'value' ]
)
const endLocationPlaceholder = pathOr(
  'Where to?',
  [ 'contentValues', 'HOMEPAGE', 'HOMEPAGE.PACKAGES.TAB.TYPEAHEAD.ENDLOCATION.PLACEHOLDER', 'value' ]
)

const focusInput = (inputRef) => {
  if (inputRef.current) {
    inputRef.current.focus()
  }
}

function trackItemSelect (data) {
  return function (item) {
    if (item.isRecent) {
      PCLNAnalytics.send({
        action: 'Packages Recent Search',
        category: ANALYTICS_CATEGORY,
        data
      })
    }
    return item
  }
}

const trackOneWayFlightLinkClick = () => {
  PCLNAnalytics.send({
    action: 'Search Form Link',
    data: 'I need a one-way flight'
  })
}

const formatOneWayHref = values => {
  const {
    tripType,
    startLocationObject = {},
    endLocationObject = {},
    startDate,
    endDate,
    roomCount,
    adultCount,
    childrenCount,
    childrenAges,
    hotelStartDate,
    hotelEndDate,
    partialStay
  } = values
  const stringifiedQuery = queryString.stringify({
    'flight-code': 'OW',
    'trip-type': tripType,
    'origin-item': startLocationObject.itemName,
    'origin-id': startLocationObject.id,
    'origin-type': startLocationObject.type,
    'origin-city': startLocationObject.cityName,
    'origin-state': startLocationObject.provinceName,
    'origin-state-code': startLocationObject.stateCode,
    'origin-country': startLocationObject.country,
    'origin-country-code': startLocationObject.countryCode,
    'destination-item': endLocationObject.itemName,
    'destination-id': endLocationObject.id,
    'destination-type': endLocationObject.type,
    'destination-city': endLocationObject.cityName,
    'destination-state': endLocationObject.provinceName,
    'destination-state-code': endLocationObject.stateCode,
    'destination-country': endLocationObject.country,
    'destination-country-code': endLocationObject.countryCode,
    start: startDate,
    end: endDate,
    rooms: roomCount,
    adults: adultCount,
    children: childrenCount,
    ages: JSON.stringify(childrenAges),
    ...(partialStay
      ? {
        'hotel-start': hotelStartDate,
        'hotel-end': hotelEndDate
      }
      : {})
  })

  return `/trips/?${stringifiedQuery}`
}

const PackagesSearchForm = ({
  values,
  touched,
  dirty,
  errors,
  handleChange,
  handleBlur,
  handleSubmit,
  setFieldValue,
  typeAheadOptions,
  calendarOpen,
  openCalendar = noop,
  closeCalendar = noop,
  hotelCalendarOpen,
  openHotelCalendar,
  closeHotelCalendar,
  tripTypesInfo,
  handleTypeaheadOnFocus,
  handleCalendarOnFocus,
  handleTravelerSelectionOnFocus,
  handleTrackingClick,
  handleFormFocus,
  handleFormBlur,
  role,
  id,
  ariaLabelledby,
  onTypeaheadKeyUp,
  withFooter
}) => {
  const isPkgOffAirportSetiOn = useSeti('PKG_OFF_AIRPORT_RC') !== 'DEFAULT'
  const {
    isMobile,
    aceContent
  } = useContext(BootstrapDataContext)

  const [isRcPickupCalendarOpen, setIsRcPickupCalendarOpen] = useState(false)
  const [isRcDropoffCalendarOpen, setIsRcDropoffCalendarOpen] = useState(false)
  const isOneWayFlightSeti = useSeti('PKG_NEW_ONE_WAY_FLIGHT_LINK_HOME_TAB') !== 'DEFAULT'
  const [recentStartLocationSearches, setRecentStartLocationSearches] = useState([])
  const [
    recentEndLocationSearches,
    setRecentEndLocationSearches
  ] = useState([])
  const [shouldUseRecentSearches, setShouldUseRecentSearches] = useState(false)

  function displayRecent (searchValue) {
    if (searchValue !== '') {
      setShouldUseRecentSearches(false)
    } else {
      setShouldUseRecentSearches(true)
    }
  }

  function saveRecent (isStartLocation) {
    return function (item) {
      if (isEmpty(item)) {
        return item
      }
      const recentSearches = isStartLocation ? recentStartLocationSearches : recentEndLocationSearches
      const isDuplicate = recentSearches.some(
        recent => item.content === recent.content
      )
      if (!isDuplicate) {
        const itemCopy = { ...item }
        itemCopy.isRecent = true
        const searchesToPreserve =
    recentSearches.length === 3 ? recentSearches.slice(0, 2) : recentSearches

        if (isStartLocation) {
          setRecentStartLocationSearches([itemCopy, ...searchesToPreserve])
        } else {
          setRecentEndLocationSearches([itemCopy, ...searchesToPreserve])
        }
      }
      return item
    }
  }

  useEffect(() => {
    try {
      const startLocationData = JSON.parse(
        localStorage.getItem('recentPackageStartLocationSearches')
      )
      const endLocationData = JSON.parse(
        localStorage.getItem('recentPackageEndLocationSearches')
      )
      if (startLocationData) {
        setRecentStartLocationSearches(startLocationData)
      }
      if (endLocationData) {
        setRecentEndLocationSearches(endLocationData)
      }
    } catch (e) {
      console.log('error fetching local results')
    }
  }, [])

  useEffect(() => {
    localStorage.setItem(
      'recentPackageStartLocationSearches',
      JSON.stringify(recentStartLocationSearches)
    )
    localStorage.setItem(
      'recentPackageEndLocationSearches',
      JSON.stringify(recentEndLocationSearches)
    )
  }, [recentStartLocationSearches, recentEndLocationSearches])

  const shouldOfferPartialStay = !isHotelPlusCar(values) && hasHotel(values)

  const calendarInput = useRef()
  const focusCalendarInput = () => focusInput(calendarInput)

  const partialStayCalendarInput = useRef()
  const focusPartialStayCalendarInput = () => focusInput(partialStayCalendarInput)

  return (
    <form
      onSubmit={handleSubmit}
      onFocus={handleFormFocus}
      onBlur={handleFormBlur}
      aria-labelledby={ariaLabelledby}
      role={role}
      id={id}
    >
      <Flex
        alignItems={['left', null, 'center']}
        justifyContent='space-between'
        flexDirection={['column', null, 'row']}
      >
        <Flex
          mx={-2}
          px={2}
          flexDirection={['column', null, 'row']}
          flexWrap='wrap'
        >
          {tripTypesInfo.map(
            typeInfo =>
              !!typeInfo && (
                <Flex mr={3} alignItems='center'>
                  <TripTypeLabel fontSize={0} fontWeight={500} nowrap>
                    <Radio
                      id={typeInfo.id}
                      name='tripType'
                      checked={values.tripType === typeInfo.tripType}
                      value={typeInfo.tripType}
                      onChange={e => {
                        handleChange(e)
                        handleTrackingClick('trip type')
                      }}
                      onBlur={handleBlur}
                    />{' '}
                    {typeInfo.typeLabel}
                  </TripTypeLabel>
                </Flex>
              )
          )}
        </Flex>
      </Flex>
      <Flex flexWrap='wrap' mx={-2}>
        {!isHotelPlusCar(values) && (
          <Box width={[1, null, 1 / 2]} px={2} mt={3}>
            <ShadowEffect enabled={!isMobile} forProperty='startLocation'>
              {({ disableShadowState }) => (
                <TypeAhead
                  name='startLocation'
                  isLargeInput
                  options={
                    shouldUseRecentSearches
                      ? recentStartLocationSearches
                      : typeAheadOptions.startLocation
                  }
                  label={startLocationPlaceholder(aceContent)}
                  placeholder={startLocationPlaceholder(aceContent)}
                  value={values.startLocation}
                  error={touched.startLocation && errors.startLocation}
                  onChange={e => {
                    handleChange(e)
                    displayRecent(e.target.value)
                  }}
                  onBlur={handleBlur}
                  setValue={setFieldValue}
                  onItemSelection={pipe(
                    trackItemSelect('Where from?'),
                    saveRecent(true),
                    typeaheadFeedback,
                    disableShadowState
                  )}
                  allowEmptySelection={dirty && !!values.startLocation}
                  onFocus={e => {
                    displayRecent(e.target.value)
                    handleTypeaheadOnFocus('to typeahead')
                  }}
                  inputProps={{
                    'data-autobot-element-id':
                      'HOME_PACKAGES_STARTLOCATION_INPUT'
                  }}
                  onTypeaheadKeyUp={onTypeaheadKeyUp}
                  disableShadowState={disableShadowState}
                />
              )}
            </ShadowEffect>
          </Box>
        )}
        <Box
          width={[1, null, isHotelPlusCar(values) ? 1 : 1 / 2]}
          px={2}
          mt={3}
        >
          <ShadowEffect enabled={!isMobile} forProperty='endLocation'>
            {({ disableShadowState }) => (
              <TypeAhead
                name='endLocation'
                isLargeInput
                options={
                  shouldUseRecentSearches
                    ? recentEndLocationSearches
                    : typeAheadOptions.endLocation
                }
                label={endLocationPlaceholder(aceContent)}
                placeholder={endLocationPlaceholder(aceContent)}
                value={values.endLocation}
                error={touched.endLocation && errors.endLocation}
                onChange={e => {
                  handleChange(e)
                  displayRecent(e.target.value)
                }}
                onBlur={handleBlur}
                setValue={setFieldValue}
                onItemSelection={pipe(trackItemSelect('Where to?'), saveRecent(false), typeaheadFeedback, disableShadowState)}
                allowEmptySelection={dirty && !!values.endLocation}
                onFocus={e => {
                  displayRecent(e.target.value)
                  handleTypeaheadOnFocus('to typeahead')
                }}
                inputProps={{
                  'data-autobot-element-id': 'HOME_PACKAGES_ENDLOCATION_INPUT'
                }}
                onTypeaheadKeyUp={onTypeaheadKeyUp}
                disableShadowState={disableShadowState}
              />
            )}
          </ShadowEffect>
        </Box>
      </Flex>
      {!isOneWayFlightSeti && values.tripType.includes('FLY') && (
        <InlineBlockBox mt={3}>
          <Link href={formatOneWayHref(values)} target='_blank' onClick={trackOneWayFlightLinkClick}>
            <Text fontWeight='bold'>I need a one-way flight</Text>
          </Link>
        </InlineBlockBox>
      )}
      <Flex
        flexWrap='wrap'
        mx={-2}
        mb={-3}
        pb={3}
        alignItems={values.partialStay ? 'flex-end' : 'flex-start'}
      >
        <Flex width={[1, null, null, null, 2 / 3]} flexWrap='wrap'>
          <CalendarWrapperBox
            px={2}
            mt={3}
            width={[1, null, 1 / 2]}
            id='flight-dates'
          >
            <ShadowEffect enabled={!isMobile} forProperty='startDate'>
              {({ disableShadowState }) => (
                <PopoverContainer
                  data-calendar
                  open={calendarOpen}
                  onDismiss={pipe(closeCalendar, disableShadowState)}
                  domSelectorForVisuallyContainedElement={
                    CALENDAR_POPOVER_SELECTOR
                  }
                >
                  <DateField
                    name='startDate'
                    label={isHotelPlusCar(values)
                      ? 'Check-in   -   Check-out'
                      : 'Departing - Returning'}
                    value={getPackagesDateRange(values)}
                    onFocus={() => {
                      openCalendar()
                      handleCalendarOnFocus()
                    }}
                    onClick={openCalendar}
                    error={parseDateError(touched, errors)}
                    hideTooltip={calendarOpen}
                    isMobile={isMobile}
                    isLargeInput
                    aria-expanded={calendarOpen ? 'true' : 'false'}
                    innerRef={calendarInput}
                    type='text'
                  />
                  <Calendar
                    monthWidthPx={300}
                    startDate={values.startDate}
                    endDate={values.endDate}
                    onChange={(e, { startDate, endDate }) => {
                      if (startDate && endDate && !isHotelPlusCar(values)) {
                        focusCalendarInput()
                      }
                      if (startDate && endDate) {
                        closeCalendar()
                        disableShadowState()
                      }
                      setFieldValue('startDate', startDate || '')
                      setFieldValue('endDate', endDate || '')
                      setFieldValue('hotelStartDate', startDate || '')
                      setFieldValue('hotelEndDate', endDate || '')
                    }}
                    useDefaultFooter={withFooter}
                    onDismiss={pipe(
                      focusCalendarInput,
                      closeCalendar,
                      disableShadowState
                    )}
                  />
                </PopoverContainer>
              )}
            </ShadowEffect>
          </CalendarWrapperBox>
          <Box px={2} mt={3} width={[1, null, 1 / 2]}>
            <ShadowEffect
              enabled={!isMobile}
              forProperty='travelers-selection-readonly-input'
              alternativeBlurHandling
            >
              {({ disableShadowState }) => (
                <TravelerSelection
                  displayProps={{
                    displayButtonProps: {
                      'data-autobot-element-id':
                        'HOME_PACKAGES_TRAVELERSELECTION_BUTTON'
                    }
                  }}
                  id='travelers'
                  isLargeInput
                  isUsingRoomControl={hasHotel(values)}
                  isUsingAdultControl
                  isUsingChildControl
                  isUsingChildAgeControl
                  isUsingControlLabel={false}
                  minAdultCount={1}
                  disclaimer={VP_DISCLAIMER}
                  initNumRooms={values.roomCount}
                  initNumAdults={values.adultCount}
                  initNumChildren={values.childrenCount || 0}
                  initChildAges={values.childrenAges || []}
                  initDisplayLabel={travelerSelectionDisplayLabel(
                    hasHotel(values),
                    values
                  )}
                  onDoneButtonClick={disableShadowState}
                  onDismiss={disableShadowState}
                  onChange={handleTravelerSelectionChange(setFieldValue)}
                  isUsingErrorTooltip
                  error={
                    errors.childAge ||
                    errors.intlLapInfant ||
                    errors.roomCapacity
                  }
                  handleInputOnFocus={() =>
                    handleTravelerSelectionOnFocus('traveler selection')
                  }
                />
              )}
            </ShadowEffect>
          </Box>
          <Box width={1} px={2} mt={3}>
            {shouldOfferPartialStay && (
              <Box width={[1, null, 1 / 2]}>
                <Label nowrap fontSize={0}>
                  <Checkbox
                    id='packages-partial-stay'
                    name='partialStay'
                    checked={values.partialStay}
                    onChange={e => {
                      handleChange(e)
                      handleTrackingClick('partial stay')
                    }}
                    onBlur={handleBlur}
                  />
                  &nbsp;I only need a hotel for part of my stay
                </Label>
                {values.partialStay && (
                  <Box my={3}>
                    <ShadowEffect enabled={!isMobile} forProperty='hotelDates'>
                      {({ disableShadowState }) => (
                        <PopoverContainer
                          data-hotel-calendar='true'
                          domSelectorForVisuallyContainedElement={
                            CALENDAR_POPOVER_SELECTOR
                          }
                          onDismiss={closeHotelCalendar}
                          open={hotelCalendarOpen}
                        >
                          <DateField
                            aria-expanded={hotelCalendarOpen ? 'true' : 'false'}
                            error={parsePartialDateError(touched, errors)}
                            isLargeInput
                            isMobile={isMobile}
                            label='Check-in - Check-out'
                            name='hotelDates'
                            onFocus={() => {
                              openHotelCalendar()
                              handleCalendarOnFocus('partial stay datefield')
                            }}
                            onClick={openHotelCalendar}
                            value={getPackagesDateRange({
                              startDate: values.hotelStartDate,
                              endDate: values.hotelEndDate
                            })}
                            innerRef={partialStayCalendarInput}
                            type='text'
                          />
                          <Calendar
                            endDate={values.hotelEndDate}
                            maxDate={values.endDate || undefined}
                            minDate={values.startDate || undefined}
                            monthsToShow={
                              values.startDate && values.endDate ? 1 : 2
                            }
                            monthWidthPx={300}
                            onChange={(e, { startDate, endDate }) => {
                              if (startDate && endDate) {
                                focusPartialStayCalendarInput()
                                closeHotelCalendar()
                                disableShadowState()
                              }
                              setFieldValue('hotelStartDate', startDate || '')
                              setFieldValue('hotelEndDate', endDate || '')
                            }}
                            onDismiss={pipe(
                              focusPartialStayCalendarInput,
                              closeHotelCalendar,
                              disableShadowState
                            )}
                            startDate={values.hotelStartDate}
                            useDefaultFooter={withFooter}
                          />
                        </PopoverContainer>
                      )}
                    </ShadowEffect>
                  </Box>
                )}
              </Box>
            )}
            {isOneWayFlightSeti && values.tripType.includes('FLY') && (
              <Box ml='5px' mt={values.partialStay ? 3 : 2}>
                <OpenTripsWithPrepopLink
                  linkText='I need a one-way flight'
                  onClick={trackOneWayFlightLinkClick}
                  values={{ flightItineraryTypeCode: 'OW', ...values }}
                />
              </Box>
            )}
          </Box>
        </Flex>
        {isPkgOffAirportSetiOn && isHotelPlusCar(values) && (
          <Flex flexWrap='wrap' alignItems='flex-start'>
            <Flex mt={2}>
              <Label nowrap fontSize={0} ml={1}>
                <Checkbox
                  id='packages-pickup-car-somewhere'
                  data-autobot-element-id='HOME_PACKAGES_RC_PICKUP_SOMEWHERE'
                  name='pickupCarSomewhere'
                  checked={values.pickupCarSomewhere}
                  onChange={e => {
                    handleChange(e)
                    handleTrackingClick('pickup car somewhere')
                  }}
                  onBlur={handleBlur}
                />
                I want to pick up my car somewhere else
              </Label>
            </Flex>
            {values.pickupCarSomewhere && (
              <Box width={[1, null, 1]} px={2} mt={2}>
                <ShadowEffect enabled={!isMobile} forProperty='rcLocation'>
                  {({ disableShadowState }) => (
                    <TypeAhead
                      name='rcLocation'
                      isLargeInput
                      options={typeAheadOptions.rcLocation}
                      label='City, Airport or Address'
                      placeholder='City, Airport or Address'
                      data-testid='packages-rc-location'
                      value={values.rcLocation}
                      error={touched.rcLocation && errors.rcLocation}
                      onChange={e => {
                        handleChange(e)
                      }}
                      onBlur={handleBlur}
                      setValue={setFieldValue}
                      disableShadowState={disableShadowState}
                      onFocus={e => {
                        displayRecent(e.target.value)
                        handleTypeaheadOnFocus('to typeahead')
                      }}
                      allowEmptySelection={dirty && !!values.rcLocation}
                      onTypeaheadKeyUp={onTypeaheadKeyUp}
                      inputProps={{
                        'data-autobot-element-id': 'HOME_PACKAGES_RCLOCATION_INPUT'
                      }}
                    />
                  )}
                </ShadowEffect>
                <Flex mt={2}>
                  <Label nowrap fontSize={0} ml={-1}>
                    <Checkbox
                      id='packages-pickup-car-include-airport'
                      name='pickupCarIncludeAirport'
                      data-autobot-element-id='HOME_PACKAGES_RC_PICKUP_INCLUDE_AIRPORT'
                      checked={values.pickupCarIncludeAirport}
                      onChange={e => {
                        handleChange(e)
                        handleTrackingClick('pickup car include airport')
                      }}
                      onBlur={handleBlur}
                    />
                    Include airport pick-up and drop-off locations
                  </Label>
                </Flex>
              </Box>
            )}
            <Flex flexWrap='wrap'>
              <CalendarWrapperBox
                px={2}
                mt={3}
                width={[1 / 2, null, 1 / 4]}
                id='rc-pickup-date'
              >
                <ShadowEffect enabled={!isMobile} forProperty='rcPickupDate'>
                  {({ disableShadowState }) => (
                    <PopoverContainer
                      data-calendar
                      open={isRcPickupCalendarOpen}
                      onDismiss={() => {
                        setIsRcPickupCalendarOpen(false)
                        disableShadowState()
                      }}
                      domSelectorForVisuallyContainedElement={
                        CALENDAR_POPOVER_SELECTOR
                      }
                    >
                      <DateField
                        name='rcPickupDate'
                        label='Pick-up'
                        value={formatDate(values.rcPickupDate)}
                        isLargeInput
                        onFocus={() => {
                          setIsRcPickupCalendarOpen(true)
                        }}
                        error={(() => {
                          if (errors.startDate) {
                            return errors.startDate.message
                          }
                        })()}
                        hideTooltip={false}
                        isMobile={isMobile}
                        aria-expanded={
                          isRcPickupCalendarOpen ? 'true' : 'false'
                        }
                        innerRef={calendarInput}
                        type='text'
                        data-autobot-element-id='HOME_PACKAGES_RC_PICKUP_DATE'
                      />
                      <Calendar
                        monthsToShow={2}
                        selectTwoDates={false}
                        maxDate={getMaxDate({ endDate: values.rcDropoffDate, maxDays: 330 })}
                        startDate={values.rcPickupDate}
                        onChange={(_, { startDate }) => {
                          if (startDate) {
                            setIsRcPickupCalendarOpen(false)
                            disableShadowState()
                          }
                          setFieldValue('rcPickupDate', startDate || '')
                        }}
                        useDefaultFooter
                        onDismiss={() => {
                          setIsRcPickupCalendarOpen(false)
                          disableShadowState()
                        }}
                      />
                    </PopoverContainer>
                  )}
                </ShadowEffect>
              </CalendarWrapperBox>
              <Box px={2} mt={3} width={[1 / 2, null, 1 / 4]}>
                <ShadowEffect enabled={!isMobile} forProperty='rcPickupTime'>
                  <TimeField
                    id='rc-pickup-time'
                    data-autobot-element-id='HOME_PACKAGES_RC_PICKUP_TIME'
                    name='rcPickupTime'
                    label='Pick-up Time'
                    style={{ height: '56px' }}
                    value={values.rcPickupTime}
                    onChange={e => {
                      setFieldValue('rcPickupTime', e.currentTarget?.value)
                      e.target.blur()
                    }}
                  />
                </ShadowEffect>
              </Box>
              <CalendarWrapperBox
                px={2}
                mt={3}
                width={[1 / 2, null, 1 / 4]}
                id='rc-dropoff-date'
              >
                <ShadowEffect enabled={!isMobile} forProperty='rcDropoffDate'>
                  {({ disableShadowState }) => (
                    <PopoverContainer
                      data-calendar
                      open={isRcDropoffCalendarOpen}
                      onDismiss={() => {
                        setIsRcDropoffCalendarOpen(false)
                        disableShadowState()
                      }}
                      domSelectorForVisuallyContainedElement={
                        CALENDAR_POPOVER_SELECTOR
                      }
                    >
                      <DateField
                        name='rcDropoffDate'
                        label='Drop-off'
                        value={formatDate(values.rcDropoffDate)}
                        isLargeInput
                        onFocus={() => {
                          setIsRcDropoffCalendarOpen(true)
                        }}
                        error={(() => {
                          if (errors.startDate) {
                            return errors.startDate.message
                          }
                        })()}
                        hideTooltip={false}
                        isMobile={isMobile}
                        aria-expanded={
                          isRcDropoffCalendarOpen ? 'true' : 'false'
                        }
                        data-autobot-element-id='HOME_PACKAGES_RC_DROPOFF_DATE'
                      />
                      <Calendar
                        monthsToShow={2}
                        selectTwoDates={false}
                        minDate={getMinDate({ startDate: values.rcPickupDate })}
                        startDate={values.rcDropoffDate}
                        onChange={(_, { startDate }) => {
                          if (startDate) {
                            setIsRcDropoffCalendarOpen(false)
                            disableShadowState()
                          }
                          setFieldValue('rcDropoffDate', startDate || '')
                        }}
                        useDefaultFooter
                        onDismiss={() => {
                          setIsRcDropoffCalendarOpen(false)
                          disableShadowState()
                        }}
                      />
                    </PopoverContainer>
                  )}
                </ShadowEffect>
              </CalendarWrapperBox>
              <Box px={2} mt={3} width={[1 / 2, null, 1 / 4]}>
                <ShadowEffect enabled={!isMobile} forProperty='rcDropoffTime'>
                  <TimeField
                    id='rc-dropoff-time'
                    name='rcDropoffTime'
                    label='Drop-off Time'
                    style={{ height: '56px' }}
                    value={values.rcDropoffTime}
                    onChange={e => {
                      setFieldValue('rcDropoffTime', e.currentTarget?.value)
                      e.target.blur()
                    }}
                    data-autobot-element-id='HOME_PACKAGES_RC_DROPOFF_TIME'
                  />
                </ShadowEffect>
              </Box>
            </Flex>
            <Flex flexWrap='wrap' width={[1, null, null, null, 1]}>
              <Box width={[1, null, 1 / 2]} px={2} mt={3}>
                <FindYourDealButton
                  isLargeInput
                  formButtonProps={{ 'data-autobot-element-id': 'HOME_PACKAGES_SUBMIT_BUTTON' }}
                  currentProduct={TRIPS_PRODUCT_TYPE}
                >
                  Find Your Deal
                </FindYourDealButton>
              </Box>
              <Box width={[1, null, 0.4]} px={2} mt={3}>
                <Text mt={2} fontSize={0}>
                  Book a hotel, flight or car with free cancellation for flexibility
                </Text>
              </Box>
            </Flex>
          </Flex>
        )}
        {(!isPkgOffAirportSetiOn || !isHotelPlusCar(values)) && (
          <Box width={[1, null, null, null, 1 / 3]} px={2} mt={3} ml='auto'>
            <FindYourDealButton
              isLargeInput
              formButtonProps={{ 'data-autobot-element-id': 'HOME_PACKAGES_SUBMIT_BUTTON' }}
              currentProduct={TRIPS_PRODUCT_TYPE}
            >
              Find Your Deal
            </FindYourDealButton>
            <Text mt={2} fontSize={0}>
              Book a hotel, flight or car with free cancellation for flexibility
            </Text>
          </Box>
        )}
      </Flex>
    </form>
  )
}

PackagesSearchForm.displayName = 'PackagesSearchForm'

PackagesSearchForm.propTypes = {
  // Formik props
  values: PropTypes.shape({
    startLocation: PropTypes.string.isRequired,
    endLocation: PropTypes.string.isRequired,
    tripType: PropTypes.string.isRequired,
    startDate: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
      .isRequired,
    endDate: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
      .isRequired,
    roomCount: PropTypes.number.isRequired,
    adultCount: PropTypes.number.isRequired,
    childrenCount: PropTypes.number.isRequired,
    childrenAges: PropTypes.array.isRequired,
    partialStay: PropTypes.bool.isRequired,
    hotelStartDate: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
      .isRequired,
    hotelEndDate: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
      .isRequired,
    pickupCarSomewhere: PropTypes.bool,
    rcLocation: PropTypes.string,
    pickupCarIncludeAirport: PropTypes.bool,
    rcPickupDate: PropTypes.string,
    rcDropoffDate: PropTypes.string,
    rcPickupTime: PropTypes.string,
    rcDropoffTime: PropTypes.string
  }).isRequired,
  touched: PropTypes.object.isRequired,
  errors: PropTypes.object.isRequired,
  handleChange: PropTypes.func.isRequired,
  handleBlur: PropTypes.func.isRequired,
  handleTravelerSelectionOnFocus: PropTypes.func.isRequired,
  handleTrackingClick: PropTypes.func.isRequired,
  handleTypeaheadOnFocus: PropTypes.func,
  handleCalendarOnFocus: PropTypes.func,
  handleSubmit: PropTypes.func.isRequired,
  setFieldValue: PropTypes.func.isRequired,
  // type ahead options/locations/dropdown items
  typeAheadOptions: PropTypes.shape({
    startLocation: PropTypes.array.isRequired,
    endLocation: PropTypes.array.isRequired,
    rcLocation: PropTypes.array.isRequired
  }),
  // UI props
  calendarOpen: PropTypes.bool,
  openCalendar: PropTypes.func.isRequired,
  closeCalendar: PropTypes.func.isRequired,
  hotelCalendarOpen: PropTypes.bool,
  openHotelCalendar: PropTypes.func.isRequired,
  closeHotelCalendar: PropTypes.func.isRequired,
  tripTypesInfo: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string,
      tripType: PropTypes.string,
      typeLabel: PropTypes.string
    })
  ),
  dirty: PropTypes.bool,
  handleFormFocus: PropTypes.func,
  handleFormBlur: PropTypes.func,
  role: PropTypes.string,
  id: PropTypes.string,
  ariaLabelledby: PropTypes.string,
  onTypeaheadKeyUp: PropTypes.func,
  withFooter: PropTypes.bool
}

PackagesSearchForm.defaultProps = {
  errors: {},
  touched: {},
  values: {
    startLocation: '',
    endLocation: '',
    startDate: '',
    endDate: '',
    roomCount: 1,
    adultCount: 2,
    childrenCount: 0,
    childrenAges: [],
    partialStay: false,
    hotelStartDate: '',
    hotelEndDate: '',
    tripType: 'STAY:FLY',
    pickupCarSomewhere: false,
    rcLocation: '',
    pickupCarIncludeAirport: true,
    rcPickupDate: '',
    rcDropoffDate: '',
    rcPickupTime: '10:00',
    rcDropoffTime: '10:00'
  },
  typeAheadOptions: {
    startLocation: [],
    endLocation: [],
    rcLocation: []
  },
  tripTypesInfo: [
    {
      id: 'tripTypeAH',
      tripType: 'STAY:FLY',
      typeLabel: 'Hotel + Flight'
    },
    {
      id: 'tripTypeAHC',
      tripType: 'STAY:FLY:DRIVE',
      typeLabel: 'Hotel + Flight + Car'
    }
  ],
  handleTypeaheadOnFocus: noop,
  handleCalendarOnFocus: noop,
  handleTravelerSelectionOnFocus: noop,
  handleTrackingClick: noop,
  withFooter: true
}

export default PackagesSearchForm
